import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  Autocomplete,
  Tooltip,
  Checkbox,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import httpRequest from "../utils/httpRequest";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ProductSearch from "./ProductSearch";

const initialNewItem = {
  product_name: "",
  hsn: "",
  pcs: "",
  quantity: "",
  unit: "pcs",
  rate: "",
  discount: 0,
  discountType: "none",
  quantity_breakup: "",
  extra_charges: 0,
  is_gst: false,
};

const CreateInvoiceDialog = ({
  open,
  onClose,
  onCloseAlert,
  onInvoiceCreated,
  type,
  editMode = false,
  invoiceData: initialInvoiceData = null,
}) => {
  useEffect(() => {
    const prefillFields = async () => {
      if (open && !editMode) {
        if (!invoiceData.invoice_date) {
          const today = new Date();
          const yyyy = today.getFullYear();
          const mm = String(today.getMonth() + 1).padStart(2, '0');
          const dd = String(today.getDate()).padStart(2, '0');
          const formattedDate = `${yyyy}-${mm}-${dd}`;
          setInvoiceData((prev) => ({
            ...prev,
            invoice_date: formattedDate,
          }));
        }
        if (
          type === "kachcha" &&
          (!invoiceData.kachcha_invoice_prefix || invoiceData.kachcha_invoice_prefix === "")
        ) {
          try {
            const res = await httpRequest("invoice/lastKachchaPrefix", "GET");
            if (res && res.prefix) {
              setInvoiceData((prev) => ({
                ...prev,
                kachcha_invoice_prefix: res.prefix,
              }));
            }
          } catch (err) {
          }
        }
      }
    };
    prefillFields();
  }, [open, type]);
  const [invoiceData, setInvoiceData] = useState({
    customer_id: "",
    firm_id: "",
    financial_year_id: "",
    kachcha_invoice_prefix: null,
    kachcha_header_user_chosen: null,
    freight: "",
    invoice_date: "",
    is_cn: false,
  });

  const [items, setItems] = useState([]);
  const [hsnList, setHsnList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [firms, setFirms] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);

  const [newItem, setNewItem] = useState(initialNewItem);
  const [editingIndex, setEditingIndex] = useState(null);
  const [highlightForm, setHighlightForm] = useState(false);

  const [isEditingFreight, setIsEditingFreight] = useState(false);
  const [editedFreight, setEditedFreight] = useState("");

  const invoiceItemsFormRef = useRef(null);
  const productNameInputRef = useRef(null);
  const freightInputRef = useRef(null);

  const isNewItemValid = () => {
    const commonValid =
      newItem.product_name.trim() !== "" &&
      newItem.pcs.toString().trim() !== "" &&
      newItem.quantity.toString().trim() !== "" &&
      newItem.unit.trim() !== "" &&
      newItem.rate.toString().trim() !== "" &&
      newItem.discount !== "" &&
      newItem.discount !== null &&
      newItem.discount !== undefined;

    const gstSpecific = type === "gst" ? newItem.hsn.trim() !== "" : true;

    return commonValid && gstSpecific;
  };

  const handleInvoiceChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInvoiceData({
      ...invoiceData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleFreightDoubleClick = () => {
    setIsEditingFreight(true);
    setEditedFreight(invoiceData.freight || "");
    setTimeout(() => {
      if (freightInputRef.current) {
        freightInputRef.current.focus();
      }
    }, 0);
  };

  const handleFreightChange = (e) => {
    setEditedFreight(e.target.value);
  };

  const handleFreightSave = () => {
    setInvoiceData((prev) => ({
      ...prev,
      freight: editedFreight,
    }));
    setIsEditingFreight(false);
  };

  const handleFreightCancel = () => {
    setIsEditingFreight(false);
    setEditedFreight(invoiceData.freight || "");
  };

  const handleNewItemChange = (e, value) => {
    if (e === "hsn") {
      setNewItem((prevItem) => ({
        ...prevItem,
        hsn: value ? value.hsn : "",
      }));
    } else {
      const { value: fieldValue, name } = e.target;

      setNewItem((prevItem) => {
        let updatedItem = { ...prevItem, [name]: fieldValue };

        if (name === "discountType") {
          if (fieldValue === "gst") {
            updatedItem.discount = 4.76;
          } else {
            updatedItem.discount = "";
          }
        }

        if (name === "discount") {
          if (prevItem.discountType === "customPercent") {
            let percentValue =
              fieldValue === ""
                ? ""
                : Math.min(Math.max(Number(fieldValue), 0), 100);
            updatedItem.discount = percentValue === "" ? "" : percentValue;
          } else if (prevItem.discountType === "customValue") {
            let maxDiscount =
              (Number(prevItem.rate) || 0) * (Number(prevItem.quantity) || 0);
            let discountValue =
              fieldValue === ""
                ? ""
                : Math.min(Math.max(Number(fieldValue), 0), maxDiscount);
            updatedItem.discount = discountValue;
          }
        }

        if (name === "unit" && fieldValue === "pcs" && prevItem.unit === "meters") {
          updatedItem.quantity_breakup = "";
        }

        if (name === "quantity_breakup" && prevItem.unit === "meters") {
          try {
            const evaluatedValue = eval(
              fieldValue.replace(/[^0-9+\-*\.]/g, "")
            );
            updatedItem.quantity_breakup = fieldValue;
            updatedItem.quantity = evaluatedValue.toFixed(2);
            const parts = fieldValue.split("+");
            let totalPcs = 0;
            for (const part of parts) {
              if (part.includes("*")) {
                const [num, mult] = part.split("*").map(Number);
                totalPcs += Math.round(mult || 0);
              } else {
                totalPcs += 1;
              }
            }

            updatedItem.pcs = totalPcs;
          } catch (error) {
            console.error("Invalid expression", error);
          }
        }

        return updatedItem;
      });
    }
  };

  const handleAddOrUpdateItem = () => {
    if (!isNewItemValid()) return;

    if (editingIndex !== null) {
      const updatedItems = items.map((item, index) =>
        index === editingIndex ? newItem : item
      );
      setItems(updatedItems);
      setEditingIndex(null);
    } else {
      setItems([...items, newItem]);
      if (invoiceItemsFormRef.current) {
        invoiceItemsFormRef.current.scrollIntoView({ behavior: "smooth" });
      }
      setTimeout(() => {
        if (productNameInputRef.current) {
          productNameInputRef.current.focus();
        }
      }, 100);
    }
    setNewItem(initialNewItem);
    setHighlightForm(false);
  };

  const handleEditItem = (index) => {
    setEditingIndex(index);
    setNewItem(items[index]);
    setHighlightForm(true);
    if (invoiceItemsFormRef.current) {
      invoiceItemsFormRef.current.scrollIntoView({ behavior: "smooth" });
      setTimeout(() => {
        if (productNameInputRef.current) {
          productNameInputRef.current.focus();
        }
      }, 300);
    }
  };

  const handleDeleteItem = (index) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
    if (editingIndex === index) {
      setEditingIndex(null);
      setNewItem(initialNewItem);
      setHighlightForm(false);
    }
  };

  const handleDuplicateItem = (index) => {
    const itemToDuplicate = { ...items[index] };
    setItems([...items, itemToDuplicate]);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setNewItem(initialNewItem);
    setHighlightForm(false);
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newItems = [...items];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    setItems(newItems);

    if (editingIndex === index) {
      setEditingIndex(index - 1);
    } else if (editingIndex === index - 1) {
      setEditingIndex(index);
    }
  };

  const handleMoveDown = (index) => {
    if (index === items.length - 1) return;
    const newItems = [...items];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    setItems(newItems);

    if (editingIndex === index) {
      setEditingIndex(index + 1);
    } else if (editingIndex === index + 1) {
      setEditingIndex(index);
    }
  };

  const handleRandomizePrefix = () => {
    const randomPrefix = Array.from({ length: 3 }, () =>
      String.fromCharCode(65 + Math.floor(Math.random() * 26))
    ).join("");
    setInvoiceData((prevData) => ({
      ...prevData,
      kachcha_invoice_prefix: randomPrefix,
    }));
  };

  const handleResetPrefix = () => {
    setInvoiceData((prevData) => ({
      ...prevData,
      kachcha_invoice_prefix: "",
    }));
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((total, item) => {
      const rate = Number(item.rate) || 0;
      const quantity = Number(item.quantity) || 0;
      const discount = Number(item.discount) || 0;
      const extra_charges = Number(item.extra_charges) || 0;

      const netAmount =
        item.discountType === "gst" || item.discountType === "customPercent"
          ? rate * quantity * (1 - discount / 100) + extra_charges
          : rate * quantity - discount + extra_charges;

      return total + netAmount;
    }, 0);

    const freight = Number(invoiceData.freight) || 0;
    const taxableAmount = subtotal + freight;
    const gst = type === "gst" ? taxableAmount * 0.05 : 0;
    const total = taxableAmount + gst;

    return { subtotal, freight, taxableAmount, gst, total };
  };

  const getDisabledReason = () => {
    if (items.length === 0) {
      return "Please add at least one item to the invoice";
    }
    if (!invoiceData.customer_id) {
      return "Please select a customer";
    }
    if (!invoiceData.firm_id) {
      return "Please select a firm";
    }
    if (type === "kachcha" && !invoiceData.is_cn) {
      if (!invoiceData.kachcha_invoice_prefix) {
        return "Please enter an invoice prefix";
      }
      if (!invoiceData.kachcha_header_user_chosen) {
        return "Please enter an invoice header";
      }
    }
    return "";
  };

  const handleSubmit = async () => {
    const payload = {
      ...invoiceData,
      items: items.map((item) => ({
        ...item,
        discount: item.discount,
        quantity_breakup: item.quantity_breakup || "",
      })),
      freight: Number(invoiceData.freight) || 0,
      gst: true,
    };

    if (type !== "gst") payload.gst = false;

    try {
      const endpoint = editMode ? `invoice/updateInvoice/${initialInvoiceData.id}` : "invoice/addInvoice";
      const method = editMode ? "PUT" : "POST";
      const createdInvoice = await httpRequest(endpoint, method, payload);
      onInvoiceCreated(createdInvoice);
      onClose();
      if (!editMode) {
      setInvoiceData({
        customer_id: "",
        firm_id: "",
        financial_year_id: "",
        freight: "",
        invoice_date: "",
        is_cn: false,
      });
        setItems([]);
        setNewItem(initialNewItem);
        setEditingIndex(null);
        setHighlightForm(false);
      }
    } catch (err) {
      console.error("Error creating/updating invoice:", err);
    }
  };

  useEffect(() => {
    if (!open) {
      setItems([]);
      setNewItem(initialNewItem);
      setEditingIndex(null);
      setHighlightForm(false);
      setInvoiceData({
        customer_id: "",
        firm_id: "",
        financial_year_id: "",
        kachcha_invoice_prefix: null,
        freight: "",
        is_cn: false,
      });
    }
  }, [open]);

  useEffect(() => {
    if (editMode && initialInvoiceData) {
      const formattedDate = initialInvoiceData.invoice_date
        ? new Date(initialInvoiceData.invoice_date).toISOString().split('T')[0]
        : '';

      setInvoiceData({
        customer_id: initialInvoiceData.customer_id,
        firm_id: initialInvoiceData.firm_id,
        financial_year_id: initialInvoiceData.financial_year_id,
        freight: initialInvoiceData.freight,
        invoice_date: formattedDate,
        kachcha_header_user_chosen: initialInvoiceData.kachcha_header_user_chosen,
        kachcha_invoice_prefix: initialInvoiceData.kachcha_prefix_user_chosen,
        is_cn: initialInvoiceData.is_cn || false,
      });
      const processedItems = (initialInvoiceData.items || []).map(item => ({
        ...item,
        is_gst: type === "kachcha" && Number(item.extra_charges) > 0
      }));
      setItems(processedItems);
    }
  }, [editMode, initialInvoiceData]);

  useEffect(() => {
    const fetchHsnList = async () => {
      try {
        const response = await httpRequest("hsn", "GET");
        setHsnList(response);
      } catch (err) {
        console.error("Error fetching HSN codes:", err);
      }
    };
    const fetchDropdownData = async () => {
      try {
        const customersData = await httpRequest("customers/getAll", "GET");
        const firmsData = await httpRequest("firm/getAll", "GET");
        const financialYearsData = await httpRequest(
          "invoice/getAllFinancialYears",
          "GET"
        );

        setCustomers(customersData);
        setFirms(firmsData);
        setFinancialYears(financialYearsData);

        const today = new Date();
        const currentFY = financialYearsData.find((fy) => {
          const start = new Date(fy.start_date);
          const end = new Date(fy.end_date);
          return today >= start && today <= end;
        });

        if (currentFY) {
          setInvoiceData((prev) => ({
            ...prev,
            financial_year_id: currentFY.id,
          }));
        }
      } catch (err) {
        console.error("Error fetching dropdown data:", err);
      }
    };

    if (open) {
      fetchDropdownData();
      fetchHsnList();
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onCloseAlert} fullWidth maxWidth="xl">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {editMode ? `Updating ${type} Invoice` : `Create ${type} Invoice`}
          </Typography>
          {type !== "gst" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2">Credit Note (Goods Return)</Typography>
              <Checkbox
                checked={invoiceData.is_cn || false}
                onChange={(e) => {
                  handleInvoiceChange({
                    target: {
                      name: "is_cn",
                      value: e.target.checked,
                    },
                  });
                  if (e.target.checked) {
                    // Clear prefix and set header when CN is enabled
                    setInvoiceData((prev) => ({
                      ...prev,
                      kachcha_invoice_prefix: "",
                      kachcha_header_user_chosen: "",
                    }));
                  }
                }}
                size="small"
              />
            </Box>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={6} sx={{ width: "100%" }}>
            <Autocomplete
              fullWidth
              options={customers}
              getOptionLabel={(option) =>
                `${option.name} (${option.account_number || "No Account Number"
                }) - ${option.location || "NA"}`
              }
              value={
                customers.find((cust) => cust.id === invoiceData.customer_id) ||
                null
              }
              onChange={(event, newValue) => {
                handleInvoiceChange({
                  target: {
                    name: "customer_id",
                    value: newValue ? newValue.id : "",
                  },
                });
              }}
              renderInput={(params) => (
                <TextField {...params} label="Customer" margin="normal" />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              sm: 3,
            }}
          >
            <FormControl fullWidth margin="normal">
              <InputLabel>Firm</InputLabel>
              <Select
                name="firm_id"
                value={invoiceData.firm_id}
                onChange={handleInvoiceChange}
                label="Firm"
              >
                {firms.map((firm) => (
                  <MenuItem key={firm.id} value={firm.id}>
                    {firm.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {type !== "gst" && (
            <>
              {!invoiceData.is_cn && (
                <>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 3,
                    }}
                  >
                    <FormControl fullWidth margin="normal">
                      <TextField
                        label="Invoice Prefix *"
                        name="kachcha_invoice_prefix"
                        type="text"
                        value={invoiceData.kachcha_invoice_prefix || ""}
                        onChange={handleInvoiceChange}
                        fullWidth
                        required
                      />
                      <Box
                        sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}
                      >
                        <small
                          style={{ color: "blue", cursor: "pointer" }}
                          onClick={handleRandomizePrefix}
                        >
                          Suggest
                        </small>
                        <small
                          style={{
                            color: "red",
                            cursor: "pointer",
                            marginLeft: "10px",
                          }}
                          onClick={handleResetPrefix}
                        >
                          Reset
                        </small>
                      </Box>
                    </FormControl>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 3,
                    }}
                  >
                    <FormControl fullWidth margin="normal">
                      <TextField
                        label="Invoice Header *"
                        name="kachcha_header_user_chosen"
                        type="text"
                        value={invoiceData.kachcha_header_user_chosen || ""}
                        onChange={handleInvoiceChange}
                        fullWidth
                        required
                      />
                    </FormControl>
                  </Grid>
                </>
              )}
            </>
          )}

          <Grid
            size={{
              xs: 12,
              sm: 3,
            }}
          >
            <FormControl fullWidth margin="normal">
              <InputLabel>Financial Year</InputLabel>
              <Select
                name="financial_year_id"
                value={invoiceData.financial_year_id}
                onChange={handleInvoiceChange}
                label="Financial Year"
              >
                {financialYears.map((year) => (
                  <MenuItem key={year.id} value={year.id}>
                    {year.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="Invoice Date"
            name="invoice_date"
            type="date"
            value={invoiceData.invoice_date}
            onChange={handleInvoiceChange}
            fullWidth
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        <Box
          sx={{
            mt: 2,
            transition: "background-color 0.5s ease",
            backgroundColor: highlightForm
              ? "rgba(255, 235, 59, 0.2)"
              : "transparent",
            padding: highlightForm ? 2 : 0,
            borderRadius: highlightForm ? 2 : 0,
          }}
          ref={invoiceItemsFormRef}
        >
          <Typography variant="h6" gutterBottom>
            Invoice Items
          </Typography>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 4,
                md: 4,
              }}
            >
              <ProductSearch
                value={newItem.product_name}
                onChange={(event, newValue) => {
                  const selectedProduct = typeof newValue === 'object' ? newValue : null;

                  handleNewItemChange({
                    target: {
                      name: "product_name",
                      value: newValue ? (typeof newValue === 'string' ? newValue : newValue.name) : "",
                    },
                  });

                  if (selectedProduct) {
                    handleNewItemChange({
                      target: {
                        name: "hsn",
                        value: selectedProduct.hsn || "",
                      },
                    });
                  } else {
                    handleNewItemChange({
                      target: {
                        name: "hsn",
                        value: "",
                      },
                    });
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  handleNewItemChange({
                    target: {
                      name: "product_name",
                      value: newInputValue,
                    },
                  });
                }}
                label="Product Name"
                inputRef={productNameInputRef}
                fullWidth
              />
            </Grid>
            {type === "gst" && (
              <Grid
                size={{
                  xs: 12,
                  sm: 4,
                  md: 4,
                }}
              >
                <FormControl fullWidth>
                  <Autocomplete
                    options={hsnList}
                    getOptionLabel={(option) =>
                      `${option.hsn} - ${option.description}`
                    }
                    value={
                      hsnList.find((hsn) => hsn.hsn === newItem.hsn) || null
                    }
                    onChange={(event, value) =>
                      handleNewItemChange("hsn", value)
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="HSN" />
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option.hsn === value.hsn
                    }
                  />
                </FormControl>
              </Grid>
            )}
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 4,
              }}
            >
              <FormControl fullWidth>
                <InputLabel>Unit</InputLabel>
                <Select
                  name="unit"
                  value={newItem.unit}
                  onChange={handleNewItemChange}
                  label="Unit"
                >
                  <MenuItem value="pcs">PCS</MenuItem>
                  <MenuItem value="meters">Meters</MenuItem>
                  <MenuItem value="joda">Joda</MenuItem>
                  <MenuItem value="dozen">Dozen</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {newItem.unit === "meters" && (
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 6,
                }}
              >
                <TextField
                  label="Quantity Breakup (e.g. 10.5+11.2)"
                  name="quantity_breakup"
                  value={newItem.quantity_breakup}
                  onChange={(e) => {
                    let value = e.target.value;

                    value = value.replace(/[^0-9+\-*\.]/g, "");

                    value = value.replace(/\*\*/g, "*");

                    value = value.replace(/\+\++/g, "+");
                    value = value.replace(/^(\+|\.)/, "");

                    handleNewItemChange({
                      target: { name: "quantity_breakup", value },
                    });
                  }}
                  fullWidth
                  required
                />
              </Grid>
            )}
            <Grid
              size={{
                xs: 6,
                sm: 4,
                md: 3,
              }}
            >
              <TextField
                label="Pcs/Than"
                name="pcs"
                type="number"
                value={newItem.pcs}
                onChange={handleNewItemChange}
                fullWidth
                disabled={newItem.unit === "meters"}
                helperText={
                  newItem.unit === "meters"
                    ? "Auto-calculated from quantity breakup"
                    : ""
                }
              />
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 4,
                md: 3,
              }}
            >
              <TextField
                label="Quantity"
                name="quantity"
                type="number"
                value={newItem.quantity}
                onChange={handleNewItemChange}
                fullWidth
                disabled={newItem.unit === "meters"}
                helperText={
                  newItem.unit === "meters"
                    ? "Auto-calculated from quantity breakup"
                    : ""
                }
              />
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 4,
                md: 3,
              }}
            >
              <TextField
                label="Rate"
                name="rate"
                type="number"
                value={newItem.rate}
                onChange={handleNewItemChange}
                fullWidth
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4,
                md: 3,
              }}
            >
              <FormControl fullWidth>
                <InputLabel>Discount Type</InputLabel>
                <Select
                  name="discountType"
                  value={newItem.discountType}
                  onChange={handleNewItemChange}
                  label="Discount Type"
                >
                  <MenuItem value="none">None</MenuItem>
                  <MenuItem value="gst">GST (4.76%)</MenuItem>
                  <MenuItem value="customPercent">Custom Percent</MenuItem>
                  <MenuItem value="customValue">Custom Value</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {type !== "gst" && (
              <>
                <Grid
                  size={{
                    xs: 12,
                    sm: 4,
                    md: 3,
                  }}
                >
                  <FormControl fullWidth>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                      <Checkbox
                        checked={newItem.is_gst}
                        onChange={(e) => {
                          handleNewItemChange({
                            target: {
                              name: "is_gst",
                              value: e.target.checked,
                            },
                          });
                          if (e.target.checked) {
                            const itemAmount = (Number(newItem.rate) || 0) * (Number(newItem.quantity) || 0);
                            const gstAmount = itemAmount * 0.05;
                            handleNewItemChange({
                              target: {
                                name: "extra_charges",
                                value: gstAmount,
                              },
                            });
                          } else {
                            handleNewItemChange({
                              target: {
                                name: "extra_charges",
                                value: 0,
                              },
                            });
                          }
                        }}
                      />
                      <Typography>GST @5%</Typography>
                    </Box>
                  </FormControl>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 4,
                    md: 3,
                  }}
                >
                  <TextField
                    label="GST"
                    name="extra_charges"
                    type="number"
                    value={newItem.extra_charges}
                    onChange={handleNewItemChange}
                    fullWidth
                    disabled
                  />
                </Grid>
              </>
            )}
            {(newItem.discountType === "customPercent" ||
              newItem.discountType === "customValue") && (
                <Grid
                  size={{
                    xs: 6,
                    sm: 4,
                    md: 3,
                  }}
                >
                  <TextField
                    label={
                      newItem.discountType === "customPercent"
                        ? "Discount (%)"
                        : "Discount Value"
                    }
                    name="discount"
                    type="number"
                    value={newItem.discount}
                    onChange={handleNewItemChange}
                    fullWidth
                  />
                </Grid>
              )}
            <Grid
              sx={{ display: "flex", gap: 2, alignItems: "center" }}
              size={12}
            >
              <Button
                variant="outlined"
                onClick={handleAddOrUpdateItem}
                color={editingIndex !== null ? "warning" : "primary"}
                disabled={!isNewItemValid()}
              >
                {editingIndex !== null ? "Update Item" : "Add Item"}
              </Button>

              {editingIndex === null && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setNewItem(initialNewItem)}
                >
                  Reset
                </Button>
              )}

              {editingIndex !== null && (
                <Button variant="text" onClick={handleCancelEdit}>
                  Cancel Edit
                </Button>
              )}
            </Grid>
          </Grid>

          {items.length > 0 && (
            <TableContainer component={Paper} sx={{ mt: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product Name</TableCell>
                    {type === "gst" && <TableCell>HSN</TableCell>}
                    <TableCell>Pcs</TableCell>
                    <TableCell>Quantity Breakup</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell>Rate</TableCell>
                    <TableCell>Discount</TableCell>
                    {type === "kachcha" && <TableCell>GST</TableCell>}
                    <TableCell>Net Amount</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor:
                          editingIndex === index
                            ? "rgba(255, 235, 59, 0.2)"
                            : "transparent",
                        transition: "background-color 0.5s ease",
                      }}
                    >
                      <TableCell>{item.product_name}</TableCell>
                      {type === "gst" && <TableCell>{item.hsn}</TableCell>}
                      <TableCell>{item.pcs}</TableCell>
                      <TableCell>{item.quantity_breakup || "-"}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.unit}</TableCell>
                      <TableCell>{item.rate}</TableCell>
                      <TableCell>
                        {item.discountType === "gst" ||
                          item.discountType === "customPercent"
                          ? `${item.discount}%`
                          : item.discount}
                      </TableCell>
                      {type === "kachcha" && <TableCell>{item.extra_charges || 0}</TableCell>}
                      <TableCell>
                        {item.discountType === "gst" ||
                          item.discountType === "customPercent"
                          ? (
                            (Number(item.rate) || 0) *
                            (Number(item.quantity) || 0) *
                            (1 - (Number(item.discount) || 0) / 100) +
                            (Number(item.extra_charges) || 0)
                          ).toFixed(2)
                          : (
                            (Number(item.rate) || 0) *
                            (Number(item.quantity) || 0) -
                            (Number(item.discount) || 0) +
                            (Number(item.extra_charges) || 0)
                          ).toFixed(2)}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Move Up">
                          <span>
                            <IconButton
                              onClick={() => handleMoveUp(index)}
                              size="small"
                              disabled={index === 0}
                            >
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move Down">
                          <span>
                            <IconButton
                              onClick={() => handleMoveDown(index)}
                              size="small"
                              disabled={index === items.length - 1}
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            onClick={() => handleEditItem(index)}
                            size="small"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicate">
                          <IconButton
                            onClick={() => handleDuplicateItem(index)}
                            size="small"
                            color="primary"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            onClick={() => handleDeleteItem(index)}
                            size="small"
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      align="right"
                      sx={{ fontWeight: "bold" }}
                    >
                      Subtotal
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      {calculateTotals().subtotal.toFixed(2)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                  <TableRow
                    sx={{
                      backgroundColor: "rgba(192, 192, 190, 0.2)",
                    }}
                  >
                    <TableCell
                      colSpan={8}
                      align="right"
                      sx={{ fontWeight: "bold" }}
                    >
                      Freight
                    </TableCell>
                    {!isEditingFreight ? (
                      <Tooltip title="Double click the value to edit" arrow>
                        <TableCell
                          onDoubleClick={handleFreightDoubleClick}
                          sx={{ fontWeight: "bold", cursor: "pointer" }}
                        >
                          {(Number(invoiceData.freight) || 0).toFixed(2)}
                        </TableCell>
                      </Tooltip>
                    ) : (
                      <TableCell sx={{ fontWeight: "bold" }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <TextField
                            size="small"
                            type="number"
                            value={editedFreight}
                            onChange={handleFreightChange}
                            inputRef={freightInputRef}
                            InputProps={{ inputProps: { min: 0 } }}
                            onKeyPress={(e) => {
                              if (e.key === "Enter") handleFreightSave();
                            }}
                          />
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={handleFreightSave}
                          >
                            <CheckIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={handleFreightCancel}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    )}
                    <TableCell />
                  </TableRow>
                  {type === "gst" && (
                    <>
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          align="right"
                          sx={{ fontWeight: "bold" }}
                        >
                          Taxable Amount
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {calculateTotals().taxableAmount.toFixed(2)}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          align="right"
                          sx={{ fontWeight: "bold" }}
                        >
                          CGST + SGST (2.5% + 2.5%)
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {calculateTotals().gst.toFixed(2)}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          align="right"
                          sx={{ fontWeight: "bold" }}
                        >
                          Total Amount (Including GST)
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {calculateTotals().total.toFixed(2)}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </>
                  )}
                  {type !== "gst" && (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        align="right"
                        sx={{ fontWeight: "bold" }}
                      >
                        Total Amount
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {calculateTotals().taxableAmount.toFixed(2)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Tooltip
          title={getDisabledReason()}
          placement="top"
          arrow
          open={getDisabledReason() ? undefined : false}
        >
          <span>
            <Button
              disabled={
                items.length === 0 ||
                !invoiceData.customer_id ||
                !invoiceData.firm_id ||
                (type === "kachcha" && !invoiceData.is_cn && (!invoiceData.kachcha_invoice_prefix || !invoiceData.kachcha_header_user_chosen))
              }
              onClick={handleSubmit}
              color="primary"
            >
              {editMode ? "Update" : "Create"}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default CreateInvoiceDialog;
